<ng-container *ngIf="comment$ | async; let comment">
  @let isMe = comment.author | isMe$ | async;

  <div class="chat-wrapper" [class.me]="isMe" [class.same-author]="isSameAuthorAsPreviousComment$ | async">
    <ng-container *ngIf="isCommentAuthorVisible$ | async">
      <div class="author-name-wrapper selectable">
        <div class="flex gap-quarter items-center font-new-default" [class.flex-row-reverse]="!isMe">
          <time-ago
            [time]="comment.created"
            [isChatComment]="true"
            [extraClass]="'chat-bubble'"
            [loopHoverTooltip]="comment.created | fancyDate: DateFormat_RelativeNowOffsetShort"
          >
          </time-ago>
          <div class="author-name">
            {{ comment.author | userName: true | async }}
          </div>
        </div>
        <loop-contact-avatar [conversationId]="comment?.parent?.id" [contact]="comment.author" />
      </div>
    </ng-container>
    <ng-container *ngTemplateOutlet="commentTpl; context: { $implicit: comment }"></ng-container>
  </div>

  <ng-template #commentTpl let-comment let-isQuoteComment="isQuoteComment">
    @if (comment | isCommentDeleted) {
      <div class="chat-comment-wrapper flex gap" [class.flex-row-reverse]="!isQuoteComment && isMe">
        <div class="chat-comment" [class.deleted]="!isQuoteComment">
          <div
            class="comment-content selectable padding-vertical-quarter padding-horizontal-half"
            [class.quote-comment]="isQuoteComment"
            [innerHTML]="(comment.author | userName: true : true : false : isMe | async) + ' deleted this message'"
          ></div>
        </div>
      </div>
    } @else {
      <div class="gap-quarter">
        <div class="chat-comment-wrapper flex flex-col gap">
          <div class="flex gap-half" [class.flex-row-reverse]="isMe">
            <div class="flex gap-half items-center flex-1-if-available" [class.justify-end]="isMe">
              <!-- Attachments stack above the text bubble so an image and its caption read as one message -->
              <div class="chat-comment-body flex-1-if-available">
                @if (comment.attachments?.resources?.length) {
                  @if (!isQuoteComment) {
                    <!-- Several attachments stack into one album so a batch of images reads as a single message -->
                    <div class="chat-comment-attachments" [class.stacked]="comment.attachments.resources.length > 1">
                      @for (attachment of comment.attachments.resources; track attachment.id ?? $index) {
                        <loop-chat-comment-attachment
                          class="flex flex-col"
                          [attachment]="attachment"
                          [siblingAttachments]="comment.attachments.resources"
                          [isStacked]="comment.attachments.resources.length > 1"
                        />
                      }
                    </div>
                  } @else if (!comment.comment) {
                    <!-- A quoted comment that carries a caption is previewed by its text instead of this placeholder -->
                    Replied to attachment
                  }
                }

                @if (comment.comment || !comment.attachments?.resources?.length) {
                  <div class="chat-comment">
                    <ng-container *ngIf="comment.quoteCommentId && !isQuoteComment">
                      <ng-container *ngIf="comment.quotedComment; else fetchQuotedCommentTpl">
                        <ng-container
                          *ngTemplateOutlet="quotedCommentTpl; context: { $implicit: comment.quotedComment }"
                        >
                        </ng-container>
                      </ng-container>
                      <ng-template #fetchQuotedCommentTpl>
                        <ng-container *ngIf="quoteComment$ | async; let quoteComment">
                          <ng-container *ngTemplateOutlet="quotedCommentTpl; context: { $implicit: quoteComment }">
                          </ng-container>
                        </ng-container>
                      </ng-template>

                      <ng-template #quotedCommentTpl let-quotedComment>
                        <div class="padding-vertical-quarter padding-horizontal-half quote-comment font-new-default">
                          <div class="flex items-center gap-quarter">
                            <loop-icon name="reply" width="14px" />

                            <span
                              >Replied to {{ quotedComment.author | userName: false : false : isMyComment | async
                              }}<span class="selectable-all hidden-selection">:</span></span
                            >
                          </div>
                          <ng-container
                            *ngTemplateOutlet="commentTpl; context: { $implicit: quotedComment, isQuoteComment: true }"
                          ></ng-container>
                        </div>
                      </ng-template>
                    </ng-container>

                    <div
                      class="comment-content padding-vertical-quarter padding-horizontal-half"
                      [class.is-emoji]="comment.comment | isEmoji"
                      [class.is-sending]="!comment.id && !commentErrorSignal()"
                      (click)="handleBodyClick($event)"
                    >
                      <ng-container *ngIf="isCollapsed$ | async; let isCollapsed">
                        <ng-container *ngIf="!comment.comment || isCollapsed.isCollapsed; else loadedCommentTpl">
                          <span
                            class="selectable-all"
                            [class.quote-comment]="isQuoteComment"
                            [innerHTML]="comment.snippet | bbcode | autolink | replaceDeeplinks | safe"
                            (copy)="bodyCopy($event)"
                          ></span
                          >&nbsp;<span
                            class="underline text-unread-color cursor-pointer"
                            (click)="isCollapsed$.next({ isCollapsed: false })"
                            >Load more...</span
                          >
                        </ng-container>
                        <ng-template #loadedCommentTpl>
                          <span
                            class="selectable-all"
                            [class.quote-comment]="isQuoteComment"
                            [innerHTML]="comment.comment | bbcode | autolink | replaceDeeplinks | safe"
                            (copy)="bodyCopy($event)"
                          ></span>
                        </ng-template>
                      </ng-container>
                      <i
                        class="edited-text"
                        *ngIf="
                          !isQuoteComment &&
                          isEditedIndicatorVisible &&
                          comment.revision &&
                          comment.revision !== '1' &&
                          comment.revision !== '0'
                        "
                        >&nbsp;(Edited)</i
                      >
                    </div>
                  </div>
                }
              </div>

              @if (commentErrorSignal(); as error) {
                <div [loopHoverTooltip]="error" class="error-icons">
                  <loop-icon class="triangle" name="alert-triangle" width="14px" loopAtom="danger" />
                  <loop-icon
                    class="retry"
                    name="arrow-rotate-right"
                    width="14px"
                    loopAtom="medium"
                    (click)="handleRetryComment(comment)"
                  />
                </div>
              }
            </div>
            <ng-container
              *ngTemplateOutlet="chatCommentActionsTpl; context: { isQuoteComment: isQuoteComment, comment: comment }"
            />
          </div>
        </div>

        <loop-comment-reactions
          [commentSignal]="comment"
          class="!flex"
          [class.justify-end]="isMe"
          [conversationIdSignal]="conversationId"
        />
      </div>
    }
  </ng-template>

  <ng-template #chatCommentActionsTpl let-comment="comment" let-isQuoteComment="isQuoteComment">
    @if (!isQuoteComment) {
      <div class="actions flex items-center gap-quarter" [class.flex-row-reverse]="isMe">
        @if (availableActions | includes: AvailableCommentActions.reaction) {
          <loop-icon
            class="clickable-icon"
            name="emoji"
            width="16px"
            height="16px"
            loopEmoji
            (emojiClick)="handleEmojiReaction($event)"
          />
        }
        <loop-icon
          *ngIf="availableActions | includes: AvailableCommentActions.copy"
          class="clickable-icon"
          (click)="copyComment(comment)"
          name="copy"
          width="16px"
          height="16px"
        ></loop-icon>
        <loop-icon
          *ngIf="availableActions | includes: AvailableCommentActions.reply"
          class="clickable-icon"
          name="reply"
          width="16px"
          height="16px"
          (click)="replyComment.emit(comment)"
        ></loop-icon>
        <loop-icon
          *ngIf="
            isMe && !comment.attachments?.resources?.length && availableActions | includes: AvailableCommentActions.edit
          "
          class="clickable-icon"
          (click)="editComment.emit(comment)"
          name="pencil"
          width="16px"
          height="16px"
        ></loop-icon>
        @if (additionalActionsSignal(); as additionalActions) {
          @for (additionalAction of additionalActions; track additionalAction) {
            <loop-icon
              class="clickable-icon"
              (click)="additionalAction.actionFn()"
              [name]="additionalAction.icon"
              width="16px"
              height="16px"
            />
          }
        }

        <loop-icon
          *ngIf="isMe && availableActions | includes: AvailableCommentActions.delete"
          class="clickable-icon"
          (click)="deleteComment(comment)"
          name="trash"
          width="16px"
          height="16px"
        ></loop-icon>
      </div>
    }
  </ng-template>
</ng-container>
